<template>
  <div class="md:max-w-screen-xl flex flex-col relative">
    <div class="hidden md:flex flex-row">
      <div class="flex flex-row cursor-pointer" @click="backToDashboard">
        <div class="flex items-center">
          <ChevronLeftIcon class="text-brand-green w-5 h-5" />
        </div>
        <div class="flex items-center">
          <p class="text-brand-dark-gray">Back to applications</p>
        </div>
      </div>
    </div>

    <template v-if="isApplicationLoading">
      <Loading />
    </template>

    <template v-else>
      <template v-if="getCurrentApplication && initialSection">
        <div class="flex md:hidden py-3 px-3 w-full">
          <DropDown
            :options="sectionOptions"
            @select="selectSection"
            :initial="initialSection"
            :showSelectedAsGray="false"
          ></DropDown>
        </div>
      </template>

      <div class="flex flex-row">
        <!-- START DESKTOP SIDE NAV -->
        <div class="hidden md:flex flex-col w-1/5 py-5 pr-5">
          <template v-if="getCurrentApplication">
            <!-- START DESKTOP HEADER -->
            <div class="rounded-lg bg-white px-5 py-5 shadow-sm hidden md:flex flex-row">
              <div class="flex flex-col">
                <h5 class="text-brand-black text-2xl py-1">
                  {{ getCurrentApplication.applicant.name }}
                </h5>
                <p class="text-sm py-1">
                  {{ getCurrentApplication.unit_address }}
                </p>

                <div class="flex items-center mt-2">
                  <div class="flex items-center relative">
                    <DownloadIcon
                      class="text-brand-dark-gray w-8 h-8 cursor-pointer hover:text-brand-green"
                      @click="downloadApplicationPackage"
                      v-if="!downloading"
                      @mouseenter="tooltip.download = true"
                      @mouseleave="tooltip.download = false"
                    />
                    <SmallLoading v-if="downloading" />

                    <ToolTip :show="tooltip.download" label="Download application package" />
                  </div>
                </div>
              </div>
            </div>
            <!-- END DESKTOP HEADER -->
          </template>
          <router-link
            :to="{
              name: 'applicant_dashboard_view_application_application',
              params: {
                id: $route.params.id
              }
            }"
            class="py-2"
          >
            <button
              class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
              :class="[
                $route.name === 'applicant_dashboard_view_application_application'
                  ? 'text-brand-green border-2 border-brand-green bg-brand-background-light-green'
                  : 'text-brand-black bg-white'
              ]"
            >
              Application
            </button>
          </router-link>

          <template v-if="getCurrentApplication">
            <router-link
              :to="{
                name: 'applicant_dashboard_view_application_report',
                params: {
                  id: $route.params.id,
                  type: profile.report_type
                }
              }"
              v-for="profile in reportProfiles"
              :key="profile.id"
              class="py-2 flex flex-row"
            >
              <button
                class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
                :class="[
                  $route.name === 'applicant_dashboard_view_application_report' &&
                  $route.params.type === profile.report_type
                    ? 'border-2 border-brand-green bg-brand-background-light-green'
                    : 'bg-white'
                ]"
              >
                <div class="flex">
                  <p
                    :class="[
                      $route.params.type === profile.report_type
                        ? 'text-brand-green'
                        : pendingStatuses.includes(profile.status)
                        ? 'text-brand-mid-gray'
                        : 'text-brand-black'
                    ]"
                  >
                    {{ profile.short_label }}
                  </p>
                  <div
                    class="flex items-center ml-1"
                    v-if="profile.status.includes('user_action_required')"
                  >
                    <ExclamationIcon class="text-brand-yellow w-5 h-5" />
                  </div>
                  <div
                    class="flex items-center ml-1"
                    v-if="pendingStatuses.includes(profile.status)"
                  >
                    <ClockIcon class="text-brand-mid-gray w-5 h-5" />
                  </div>

                  <div class="flex items-center ml-1" v-if="errorStatuses.includes(profile.status)">
                    <XIcon class="text-brand-red w-5 h-5" />
                  </div>
                </div>
              </button>
            </router-link>
          </template>
        </div>
        <!-- END DESKTOP SIDE NAV -->

        <div class="flex flex-col w-full md:w-4/5 py-5 bg-brand-light-gray">
          <router-view
            :key="`${$route.name}-${$route.params.id}${
              $route.params.type ? `-${$route.params.type}` : ''
            }`"
          />
        </div>
      </div>

      <template v-if="getCurrentApplication">
        <router-link
          tag="div"
          :to="{
            name: 'applicant_dashboard_view_application_application',
            params: { id: getCurrentApplication.id }
          }"
          v-if="$route.name !== 'applicant_dashboard_view_application_application'"
          class="md:hidden fixed z-10 left-1 bottom-3 py-2 px-2 rounded bg-brand-green shadow-lg cursor-pointer"
        >
          <div class="flex items-center">
            <ArrowLeftIcon class="text-white w-5 h-5 mr-1" />
            <p class="text-white text-sm">Back to application</p>
          </div>
        </router-link>
      </template>
    </template>
  </div>

  <template v-if="getCurrentApplication">
    <ApplicationHistoryModal
      :showModal="showHistoryModal"
      @close="closeHistoryModal"
      :app="getCurrentApplication"
    />
  </template>

  <ActionToast ref="viewApplicationToast" />
</template>
<script>
import { mapGetters } from "vuex"
import moment from "moment"
import _ from "lodash"

import {
  ChevronLeftIcon,
  ClockIcon,
  ExclamationIcon,
  ArrowLeftIcon,
  XIcon,
  DownloadIcon
} from "@heroicons/vue/outline"

import ApplicationHistoryModal from "@/views/dashboard/view_application/components/ApplicationHistoryModal.vue"

import { APPLICATION_GET, APPLICATION_PACKAGE_DOWNLOAD, USER_SET_CONTEXT } from "@/store/types"

export default {
  components: {
    ChevronLeftIcon,
    ClockIcon,
    ExclamationIcon,
    XIcon,
    ArrowLeftIcon,
    ApplicationHistoryModal,
    DownloadIcon
  },
  created() {
    if (this.getCurrentUserContext !== "applicant") {
      this.$store.commit(USER_SET_CONTEXT, "applicant")
    }
  },
  async mounted() {
    const id = this.$route.params.id

    if (this.$route.name === "applicant_dashboard_view_application") {
      this.$router.push({
        name: "applicant_dashboard_view_application_application",
        params: { id }
      })
      return
    }

    if (!this.getCurrentApplication || this.getCurrentApplication.id !== id) {
      await this.loadApplication()
    }

    if (!this.initialSection) {
      const value = this.$route.name.split("applicant_dashboard_view_application_")[1]
      if (value === "application") {
        this.initialSection = { label: _.capitalize(value), value }
      } else {
        const type = this.$route.params.type
        this.initialSection = {
          label: _.capitalize(type.replace("_", " ")),
          value: type
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      "getCurrentApplication",
      "getCurrentUser",
      "isApplicationLoading",
      "getCurrentUserContext"
    ]),
    reportProfiles() {
      if (!this.getCurrentUser || !this.getCurrentApplication) return []

      const formatted = _.orderBy(
        [...this.getCurrentApplication.reports].map((r) => {
          if (r.report_type === "credit") {
            r.order = 1
            return r
          }
          if (r.report_type === "eviction") {
            r.order = 2
            return r
          }

          if (r.report_type === "criminal") {
            // Do not show criminal report option to applicants when not ready
            if (r.status === "ready_user_action_required") {
              return null
            }
            r.order = 3
            return r
          }

          if (r.report_type.includes("employment") || r.report_type.includes("income")) {
            r.order = 4
            return r
          }

          if (r.report_type.includes("landlord")) {
            r.order = 5
            return r
          }
        }),
        ["order"],
        ["asc"]
      ).filter(Boolean)

      return formatted
    },
    sectionOptions() {
      if (!this.getCurrentApplication) {
        return []
      }
      return [
        { label: "Application", value: "application" },
        ...this.reportProfiles.map((p) => {
          return { label: p.short_label, value: p.report_type }
        })
      ]
    }
  },
  data() {
    return {
      showHistoryModal: false,
      downloading: false,
      initialSection: null,
      showRejectModal: false,
      pendingStatuses: ["sent_payment_request", "new", "pending", "requested"],
      errorStatuses: ["error", "fatal"],
      tooltip: {}
    }
  },
  methods: {
    formatDecisionTime(date) {
      return moment(date).format("MM/DD/YY @ h:mma")
    },
    async loadApplication() {
      const id = this.$route.params.id
      await this.$store.dispatch(APPLICATION_GET, { id })
    },
    backToDashboard() {
      this.$router.push({ name: "applicant_dashboard_applications" })
    },
    openHistoryModal() {
      this.showHistoryModal = true

      if (this.showMobileModal) this.showMobileModal = false
    },
    closeHistoryModal() {
      this.showHistoryModal = false
    },
    async downloadApplicationPackage() {
      this.downloading = true
      if (this.isApplicationLoading) {
        return
      }
      try {
        const errors = await this.$store.dispatch(APPLICATION_PACKAGE_DOWNLOAD)

        if (errors) {
          this.$refs.viewApplicationToast.show({
            type: "info",
            hasClose: true,
            content:
              "One or more items from this application failed to download successfully. Contact support@rentapplication.net for further assistance.",
            icon: "check"
          })
        } else {
          this.$refs.viewApplicationToast.show({
            type: "success",
            hasClose: true,
            content: "Package downloaded",
            icon: "check"
          })
        }
      } catch (err) {
        const that = this
        setTimeout(() => {
          that.$refs.viewApplicationToast.show({
            type: "error",
            hasClose: true,
            content: "Package download failed",
            icon: "x"
          })
        }, 10)
      }

      this.downloading = false
    },
    selectSection(section) {
      if (section.value === "application") {
        this.$router.push({
          name: `applicant_dashboard_view_application_application`,
          params: { id: this.getCurrentApplication.id }
        })
      } else {
        this.$router.push({
          name: "applicant_dashboard_view_application_report",
          params: { id: this.getCurrentApplication.id, type: section.value }
        })
      }
    }
  }
}
</script>
